/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { AnalyticsConstants, Diagnostics, Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import { ScreenType } from 'client-code/previews';
import { AiringOffer } from 'client-code/airing';
import { AppUtils } from '../AppUtils';
import { GuideCell } from '../entities/GuideCell';
import { Channel } from '../entities/Channel';
import { ServicesClientImpl } from '../impls/ServicesClientImpl';
import { TOfferPreview } from '../types/TOfferPreview';
import { IVULiveTvPayload } from './IVULiveTvPayload';
import { Log } from './Log';
import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { DeviceModel } from '../device/DeviceModel';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { Keys } from '../navigation/Keys';
import { PreSignInError } from '../error/PreSignInError';
import { SignInError } from '../error/SignInError';
import { Storage } from '../storage/Storage';
import { GuideError, SignOutError } from '../error';

export type ErrorEventData = {
    code: string;
    message: string;
    source: string;
    type: string;
};

export type UserActionEventData = {
    actionName: string;
    action: string;
    screen: string;
    selection: string;
};

export class VuLogger {
    private static TAG = 'VuLogger';

    // flag to check that watchStopped event should only log if watchStarted
    private static isWatchStarted: boolean = false;

    // flag to check sessionId should be present in watchStartedEvent
    private static logWatchStartedEventFlag: boolean = false;

    // setting playUrl for errorEvent
    public static playUrl: string | undefined = undefined;

    // flag to log streaming recovery if session error recovers
    public static sessionErrorRecoveryFlag: boolean = false;

    private static mUserId: string;

    private static sessionId: string | undefined = undefined;

    // flag to log streaming recovery only when player recovers
    static streamingRecoveryFlag: boolean = false;

    private static guideEntryTime: number = Date.now();

    private static signinEntryTime: number = Date.now();

    private static globalParams: Payload;

    static ErrorEventSource = {
        STREAMING_SESSION_ERROR: 'streamingSessionError',
    };

    private static LiveTvPayloadDefaultValues: IVULiveTvPayload = {
        callSign: undefined,
        channelName: undefined,
        duration: undefined,
        episodeNumber: undefined,
        episodeTitle: undefined,
        genre: undefined,
        seasonNumber: undefined,
        sessionId: undefined,
        startTime: undefined,
        stationId: undefined,
        viewingSessionId: undefined,
        year: undefined,
    };

    static NotifyEventSourceType = {
        ADULT_OSD: 'AdultOSD',
        BLACKLISTED_OSD: 'BlacklistedOSD',
        CHANNEL_OPTIONS_OVERLAY: 'ChannelOptionsOverlay',
        DEVICE_TYPE_RESTRICTED_OSD: 'DeviceTypeRestricted',
        ERROR_OVERLAY: 'ErrorOverlay',
        EXIT_CONFIRMATION_OVERLAY: 'ExitConfirmationOverlay',
        NO_NETWORK_CONNECTION_OSD: 'NoNetworkConnectionOSD',
        NO_NETWORK_CONNECTION_OVERLAY: 'NoNetworkConnectionOverlay',
        OUT_OF_HOME_OSD: 'OutOfHomeOSD',
        OUT_OF_REGION_OSD: 'OutOfRegionOSD',
        STILL_THERE_OSD: 'StillThereOSD',
        TEMPORARY_GUIDE_ISSUE_OVERLAY: 'TemporaryGuideIssueOverlay',
        UNSUBSCRIBED_OSD: 'UnsubscribedOSD',
    };

    static NotifyEventSourceName = {
        ADULT_SHOW: 'Adult Show',
        BLACKLISTED: 'Blacklisted',
        CHANNEL_OPTIONS: 'Channel Options',
        DEVICE_TYPE_RESTRICTED: 'Device Type Restricted',
        EXIT_CONFIRMATION: 'Exit Kaveri?',
        NO_NETWORK_CONNECTION: 'No Network Connection',
        OUT_OF_HOME: 'Out Of Home',
        OUT_OF_REGION: 'Out Of Region',
        PLAYER_ERROR: 'Player Error',
        SESSION_ERROR: 'Session Error',
        STILL_THERE: 'Still There',
        TEMPORARY_GUIDE_ISSUE: 'Temporary Guide Issue',
        UNSUBSCRIBED_CHANNEL: 'Unsubscribed Channel',
    };

    private static OverlayNotificationEvents = {
        [OverlayTypes.CHANNEL_OPTIONS]: {
            sourceType: VuLogger.NotifyEventSourceType.CHANNEL_OPTIONS_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.CHANNEL_OPTIONS,
        },
        [OverlayTypes.EXIT_CONFIRMATION]: {
            sourceType: VuLogger.NotifyEventSourceType.EXIT_CONFIRMATION_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.EXIT_CONFIRMATION,
        },
        [OverlayTypes.NO_NETWORK]: {
            sourceType: VuLogger.NotifyEventSourceType.NO_NETWORK_CONNECTION_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.NO_NETWORK_CONNECTION,
        },
        [OverlayTypes.TEMPORARY_GUIDE_ISSUE]: {
            sourceType: VuLogger.NotifyEventSourceType.TEMPORARY_GUIDE_ISSUE_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.TEMPORARY_GUIDE_ISSUE,
        },
    };

    static StreamingRecoveryType = {
        NETWORK_ERROR: 'NETWORK_ERROR',
        PLAYBACK_ERROR: 'PLAYBACK_ERROR',
        SESSION_ERROR: 'SESSION_ERROR',
    };

    public static TrickplayMode = {
        FAST_FORWARD_1: 'FAST_FORWARD_1',
        FAST_FORWARD_2: 'FAST_FORWARD_2',
        FAST_FORWARD_3: 'FAST_FORWARD_3',
        PLAY: 'PLAY',
        PAUSE: 'PAUSE',
        REWIND_1: 'REWIND_1',
        REWIND_2: 'REWIND_2',
        REWIND_3: 'REWIND_3',
    };

    public static UserActionEventActionName = {
        AUDIO_LANGUAGE: 'AUDIO_LANGUAGE',
        CHANNEL_OPTIONS: 'CHANNEL_OPTIONS',
        ERROR_OVERLAY: 'ERROR_OVERLAY',
        EXIT_CONFIRMATION: 'EXIT_CONFIRMATION',
        LANGUAGE_NOT_SUPPORTED: 'LANGUAGE_NOT_SUPPORTED',
        NO_NETWORK: 'NO_NETWORK',
        SUBTITLE_AND_CC: 'SUBTITLE_AND_CC',
        TEMPORARY_GUIDE_ISSUE: 'TEMPORARY_GUIDE_ISSUE',
    };

    public static UserActionEventAction = {
        CANCEL: 'cancel',
        OK: 'ok',
        UPDATE: 'update',
    };

    public static UserActionEventScreen = {
        GUIDE: 'Guide',
        LIVE_TV: 'Live TV',
        OVERLAYS: 'Overlays',
    };

    public static UserSelection = {
        OK: 'Ok',
        CANCEL: 'Cancel',
    };

    static WatchStoppedEndReason = {
        CHANNEL_CHANGE: 'CHANNEL_CHANGE',
        END_OF_CONTENT: 'END_OF_CONTENT',
        ENDED_BY_USER: 'ENDED_BY_USER',
        VIDEO_WINDOW_OFF: 'VIDEO_WINDOW_OFF',
        VISIBILITY_BEHIND: 'VISIBILITY_BEHIND',
    };

    static AppEntryUserTypes = {
        NEW_USER: 'newUser',
        USER_RETURN: 'userReturn',
    };

    static AppEntryTypes = {
        FIRST_TIME_LAUNCH: 'firstTimeLaunch',
        NONE: 'none',
    };

    static AppExitActions = {
        EXIT: 'exit',
        SUSPEND: 'suspend',
    };

    private static liveTvCommonPayload: IVULiveTvPayload = {
        ...VuLogger.LiveTvPayloadDefaultValues,
    };

    // method to set SessionId
    static setSessionId = (sessionId: string) => {
        VuLogger.sessionId = sessionId;
        VuLogger.liveTvCommonPayload.sessionId = VuLogger.sessionId;
        if (VuLogger.logWatchStartedEventFlag) {
            VuLogger.logWatchStartedEvent();
        }
    };

    // clear SessionId at watchStopped Event
    static clearSessionId = () => {
        VuLogger.sessionId = undefined;
    };

    static createPayload = (
        guideCell: GuideCell,
        channel: Channel,
        offerPreview: { [key: string]: any },
    ): Payload => {
        const payload: Payload = new Payload();
        const { callSign, name, stationId } = channel;
        const { startTime, duration, movieYear, contentType, title } = guideCell;
        const contentPreview = offerPreview && offerPreview?.contentPreview;
        const categoryLabel = contentPreview && contentPreview?.categoryLabel;
        const episodeNumber = contentPreview && contentPreview?.episodeNumber;
        payload.add('callSign', callSign || '');
        payload.add('name', name || '');
        payload.add('contentType', contentType || '');
        payload.add('duration', duration || '');
        payload.add('episodeTitle', title || undefined);
        payload.add('genre', categoryLabel || '');
        payload.add('startTime', startTime.getTime() || '');
        payload.add('stationId', stationId || '');
        payload.add('year', movieYear || undefined);
        payload.add('episodeNumber', episodeNumber || undefined);
        return payload;
    };

    static createPayloadForWatchNow = (
        guideCells: GuideCell[],
        channel: Channel,
    ): Promise<Payload> => {
        const currentIndex = VuLogger.evaluateHorizontalIndex(guideCells);
        const offerId = guideCells && guideCells[currentIndex]?.offerId;
        const services = new ServicesClientImpl();
        return new Promise((resolve) => {
            services
                .fetchOfferPreview(offerId, ScreenType.GuideHeader)
                .then((response: TOfferPreview) => {
                    resolve(
                        VuLogger.createPayload(
                            guideCells[currentIndex],
                            channel,
                            response.offerPreview,
                        ),
                    );
                })
                .catch(() => {
                    resolve(VuLogger.createPayload(guideCells[currentIndex], channel, {}));
                });
        });
    };

    private static evaluateHorizontalIndex = (guideCells: GuideCell[]): number => {
        return guideCells?.findIndex((guideCell: GuideCell) => {
            const currentTimestamp = new Date();
            const { startTime, duration } = guideCell;
            const mstartTime =
                startTime.getTime() < currentTimestamp.getTime() ? currentTimestamp : startTime;
            const endTime = AppUtils.addSecondsToDateTime(startTime, duration);

            return currentTimestamp >= mstartTime && currentTimestamp <= endTime;
        });
    };

    static logAppError = (error: GuideError | SignOutError) => {
        const payload: Payload = new Payload();
        const errorPayload = {
            code: error.getCode(),
            message: error.getMessage(),
            source: error.TAG,
            type: error.getType(),
        };
        payload.add('error', errorPayload);
        payload.add('userId', VuLogger.mUserId);
        VU.appError(payload);
    };

    static logErrorEvent = (error: ErrorEventData) => {
        const payload: Payload = new Payload({ ...VuLogger.liveTvCommonPayload });
        payload.add('error', { ...error, url: VuLogger.playUrl });
        payload.add('userId', VuLogger.mUserId);
        VU.errorEvent(payload);
    };

    static logIdentifyEvent = (userId: string) => {
        VuLogger.mUserId = userId;
        const payload: Payload = new Payload({ userId: VuLogger.mUserId });
        VU.identify(payload);
    };

    static logAppEntryEvent = () => {
        const payload: Payload = new Payload();
        let entryType = VuLogger.AppEntryTypes.FIRST_TIME_LAUNCH;
        let userType = VuLogger.AppEntryUserTypes.NEW_USER;

        if (Storage.getInstance()?.getItem(Storage.Keys.IS_APP_ALREADY_LAUNCHED)) {
            entryType = VuLogger.AppEntryTypes.NONE;
            userType = VuLogger.AppEntryUserTypes.USER_RETURN;
        } else Storage.getInstance()?.setItem(Storage.Keys.IS_APP_ALREADY_LAUNCHED, true);
        payload.add('entryType', entryType);
        payload.add('userType', userType);
        payload.add('userId', VuLogger.mUserId);
        VU.appEntryEvent(payload);
    };

    static logAppExitEvent = (action: string) => {
        const payload: Payload = new Payload({ action });
        payload.add('userId', VuLogger.mUserId);
        VU.appExitEvent(payload);
    };

    static logGuideScreenEntryEvent = () => {
        VuLogger.guideEntryTime = Date.now();
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    static logGuideScreenExitEvent = () => {
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.EXIT,
        );
        payload.add(
            AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
            Date.now() - VuLogger.guideEntryTime,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    static logGuideScreenPauseEvent = () => {
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.SUSPEND,
        );
        payload.add(
            AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
            Date.now() - VuLogger.guideEntryTime,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    static logGuideScreenResumeEvent = () => {
        VuLogger.guideEntryTime = Date.now();
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.RESUME,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    static logLoginEvent = (msoServiceId: string) => {
        const payload: Payload = new Payload();
        payload.add('msoServiceId', msoServiceId);
        payload.add('userId', VuLogger.mUserId);
        VU.loginEvent(payload);
    };

    static logLoginFailureEvent = (error: SignInError | PreSignInError) => {
        const payload: Payload = new Payload();
        const errorPayload = {
            code: error.getCode(),
            message: error.getMessage(),
            source: error.TAG,
            type: error.getType(),
        };
        payload.add('userId', VuLogger.mUserId);
        payload.add('error', errorPayload);
        VU.loginFailureEvent(payload);
    };

    static logNotifyEvent = (sourceType: string, sourceName: string) => {
        const notifyEventPayload = new Payload();
        notifyEventPayload.add('sourceType', sourceType);
        notifyEventPayload.add('sourceName', sourceName);
        notifyEventPayload.add('userId', VuLogger.mUserId);
        VU.notifyEvent(notifyEventPayload);
    };

    static logGuideNotificationEvent = (overlayType: number) => {
        if (overlayType in VuLogger.OverlayNotificationEvents) {
            const { sourceType, sourceName } = VuLogger.OverlayNotificationEvents[overlayType];
            VuLogger.logNotifyEvent(sourceType, sourceName);
        }
    };

    static logKeyEvent = (key: Keys) => {
        const keyEventPayload = new Payload();
        keyEventPayload.add('buttonName', key.keyCode);
        keyEventPayload.add('isLongPress', key.type === 'longkeypressedstart');
        keyEventPayload.add('userId', VuLogger.mUserId);
        if (key.type !== 'longkeypressedstop') VU.keyEvent(keyEventPayload);
    };

    static logSignInScreenEntryEvent = () => {
        VuLogger.signinEntryTime = Date.now();
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, 'signInScreen');
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    static logSignInScreenExitEvent = () => {
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, 'signInScreen');
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.EXIT,
        );
        payload.add(
            AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
            Date.now() - VuLogger.signinEntryTime,
        );
        payload.add('userId', VuLogger.mUserId);
        VuLogger.signinEntryTime = Date.now();
        VU.screen(payload);
    };

    static logStreamingRecovery = (recoveryType: string) => {
        // making payload with common payload
        const commonPayload: Payload = new Payload(VuLogger.liveTvCommonPayload);
        const streamingRecoveryPayload = commonPayload.merge(commonPayload);
        streamingRecoveryPayload.add('recoveryType', recoveryType);

        // logging streamingRecovery Event
        VU.streamingRecovery(streamingRecoveryPayload);
    };

    static logTrickPlayEvent = (trickplayMode: string, currentTime: number) => {
        const payload: Payload = new Payload(VuLogger.liveTvCommonPayload);
        const trickPlayPayload = payload.merge(payload);

        trickPlayPayload.add('trickplayAction', trickplayMode);
        trickPlayPayload.add('trickplayActionTime', Date.now());
        trickPlayPayload.add('userId', VuLogger.mUserId);
        if (VuLogger.liveTvCommonPayload.startTime && currentTime) {
            const startTime = Math.floor(
                new Date(VuLogger.liveTvCommonPayload.startTime).getTime() /
                    MILLISECONDS_IN_A_SECOND,
            );
            const currentTimeInUnixSeconds = Math.floor(currentTime / MILLISECONDS_IN_A_SECOND);
            if (currentTimeInUnixSeconds >= startTime) {
                trickPlayPayload.add('offset', currentTimeInUnixSeconds - startTime);
            }
        }
        VU.trickPlayEvent(trickPlayPayload);
    };

    static logUserActionEvent = (data: UserActionEventData) => {
        const userActionPayload: Payload = new Payload();
        userActionPayload.add('actionName', data.actionName);
        userActionPayload.add('action', data.action);
        userActionPayload.add('screen', data.screen);
        userActionPayload.add('selection', data.selection);
        userActionPayload.add('userId', VuLogger.mUserId);
        VU.userActionEvent(userActionPayload);
    };

    private static logUserActionEventForGuide = (
        actionName: string,
        action: string,
        selection: string,
    ) => {
        VuLogger.logUserActionEvent({
            actionName,
            action,
            screen: VuLogger.UserActionEventScreen.GUIDE,
            selection,
        });
    };

    static logChannelOptionOverlayOkUserAction = (selectionText: string) => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.CHANNEL_OPTIONS,
            VuLogger.UserActionEventAction.OK,
            selectionText,
        );
    };

    static logChannelOptionOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.CHANNEL_OPTIONS,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    static logExitConfirmationOverlayOkUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.EXIT_CONFIRMATION,
            VuLogger.UserActionEventAction.OK,
            VuLogger.UserSelection.OK,
        );
    };

    static logExitConfirmationOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.EXIT_CONFIRMATION,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    static logLanguageNotSupportedOverlayUserActionEvent = () => {
        VuLogger.logUserActionEvent({
            actionName: VuLogger.UserActionEventActionName.LANGUAGE_NOT_SUPPORTED,
            action: VuLogger.UserActionEventAction.OK,
            screen: VuLogger.UserActionEventScreen.OVERLAYS,
            selection: VuLogger.UserSelection.OK,
        });
    };

    static logNoNetworkOverlayOkUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.NO_NETWORK,
            VuLogger.UserActionEventAction.OK,
            VuLogger.UserSelection.OK,
        );
    };

    static logNoNetworkOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.NO_NETWORK,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    static logTemporaryGuideIssueOverlayOkUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.TEMPORARY_GUIDE_ISSUE,
            VuLogger.UserActionEventAction.OK,
            VuLogger.UserSelection.OK,
        );
    };

    static logTemporaryGuideIssueOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.TEMPORARY_GUIDE_ISSUE,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    static logWatchStartedEvent = () => {
        // check for sessionId
        if (!VuLogger.sessionId) {
            // setting logWatchStartedEventFlag as sessionId is not present
            VuLogger.logWatchStartedEventFlag = true;

            // setting isWatchStarted as watchStarted did not happen
            VuLogger.isWatchStarted = false;
        } else {
            const watchStartedPayload = new Payload(VuLogger.liveTvCommonPayload);
            watchStartedPayload.add('userId', VuLogger.mUserId);
            VU.watchStartedEvent(watchStartedPayload);

            // setting isWatchStarted as watchStarted
            VuLogger.isWatchStarted = true;

            // setting logWatchStartedEventFlag as sessionId is present now
            VuLogger.logWatchStartedEventFlag = false;
        }
    };

    static logWatchStoppedEvent = (reason: string) => {
        // watchStopped should not log without watchStart
        if (!VuLogger.isWatchStarted) {
            Log.w(
                VuLogger.TAG,
                'Ignored. - Trying to log watchStopped event without watchStarted ...',
            );
            Diagnostics.warn(
                VuLogger.TAG,
                'Ignored. - Trying to log watchStopped event without watchStarted ...',
            );
            return;
        }

        const payload: Payload = new Payload(VuLogger.liveTvCommonPayload);
        const watchStoppedPayload = payload.merge(payload);
        watchStoppedPayload.add('reason', reason);
        watchStoppedPayload.add('userId', VuLogger.mUserId);
        VU.watchStoppedEvent(watchStoppedPayload);

        // setting isWatchStarted as watchStopped is logged
        VuLogger.isWatchStarted = false;

        // Clearing sessionId before next watchStarted
        if (reason !== VuLogger.WatchStoppedEndReason.END_OF_CONTENT) VuLogger.clearSessionId();
    };

    static setGlobalParams = (globalParams: Payload) => {
        VuLogger.globalParams = globalParams;
        VU.setGlobals(globalParams);
    };

    static updateGlobalParams = (params: { [key: string]: any }) => {
        const payload = new Payload(params);
        const updatedGlobalPayload = VuLogger.globalParams.merge(payload);
        VuLogger.setGlobalParams(updatedGlobalPayload);
    };

    static updateLiveTvPayload = (channel: Channel, airingOffer: AiringOffer[]) => {
        VuLogger.liveTvCommonPayload.callSign = channel.callSign;
        VuLogger.liveTvCommonPayload.channelName = channel.name;
        VuLogger.liveTvCommonPayload.duration = airingOffer[0].duration;
        VuLogger.liveTvCommonPayload.episodeNumber = airingOffer[0].banner?.episodeNumber;
        VuLogger.liveTvCommonPayload.episodeTitle = airingOffer[0].banner?.subtitle;
        VuLogger.liveTvCommonPayload.genre = airingOffer[0].logging.genre;
        VuLogger.liveTvCommonPayload.seasonNumber = airingOffer[0].banner?.seasonNumber;
        VuLogger.liveTvCommonPayload.sessionId = VuLogger.sessionId;
        VuLogger.liveTvCommonPayload.startTime = new Date(airingOffer[0].startTime).getTime();
        VuLogger.liveTvCommonPayload.stationId = airingOffer[0].stationId;
        VuLogger.liveTvCommonPayload.viewingSessionId = DeviceModel.generateUniqueId(
            `${Date.now()}`,
        );
        VuLogger.liveTvCommonPayload.year = airingOffer[0].banner?.movieYear;
    };
}
